import {
  AttachedField,
  AttachedForm,
  AttachedFormDto,
  DacDetails,
  DacResponseSoft,
  DacListRequest,
  DacUpdateConfigDto,
  DacUpdateDto,
  DetachedFormDto,
  DynamicDocCopy,
  PreviewAaoDynaDoc,
  PreviewDocDac,
  PreviewDocDacResponse,
} from './../../types/dac.types';
import {
  DacCreateDto,
  DacDocsFormResponse,
  DacResponse,
  IResponsePaginate,
  Ppm,
  IResponse,
  BaseQueryParams,
  WithMetadata,
  DocSubmission,
  DacDocSubmission,
  DacDocSubmissionDto,
  DynamicForm,
  Currency,
  TaskHistory,
  ParamGeneralDacValue,
  ParamGeneralDacValueDto,
} from '@/types';
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PpmQuery } from '../ppm/ppm.service';

@Injectable({
  providedIn: 'root',
})
export class DacService {
  BASEURL = `${environment.api.dacUrl}`;
  BASEURL_PPM = `${environment.api.ppmUrl}`;
  BASEURL_PARAMS = `${environment.api.paramsUrl}`;
  httpClient = inject(HttpClient);

  createDac(dacCreateDto: DacCreateDto): Observable<DacResponse> {
    return this.httpClient.post<DacResponse>(`${this.BASEURL}/dac/create`, dacCreateDto);
  }

  createDacFromAo(dacCreateDto: DacCreateDto): Observable<DacResponse> {
    return this.httpClient.post<DacResponse>(`${this.BASEURL}/dac/create-from-ao`, dacCreateDto);
  }

  getPublishedDacsByRealisation(realisationId: number, realisationVer: number): Observable<any[]> {
    return this.httpClient.get<any[]>(`${this.BASEURL}/dac/published/${realisationId}/${realisationVer}`);
  }

  getDacs(): Observable<IResponsePaginate<DacResponse>> {
    return this.httpClient.get<IResponsePaginate<DacResponse>>(`${this.BASEURL}/dac`);
  }

  getDacsByAo(aoId: number): Observable<IResponsePaginate<DacResponse>> {
    return this.httpClient.get<IResponsePaginate<DacResponse>>(`${this.BASEURL}/dac/appel-offre/${aoId}/latest`);
  }

  getDacTaskHistory(processInstanceId: string): Observable<TaskHistory[]> {
    return this.httpClient.get<TaskHistory[]>(`${this.BASEURL}/flowable/task-history/${processInstanceId}`);
  }

  getDacById(dacId: number, dacVer?: number): Observable<DacResponse> {
    return this.httpClient.get<DacResponse>(`${this.BASEURL}/dac/docs/${dacId}/${dacVer}`);
  }

  submitForms(dacUpdateDto: DacUpdateDto): Observable<DacResponse> {
    return this.httpClient.post<DacResponse>(`${this.BASEURL}/dac-docs/batch-versioning`, dacUpdateDto);
  }

  updateDacDocs(id: number, dacUpdateDto: DacUpdateDto): Observable<DacResponse> {
    return this.httpClient.put<DacResponse>(`${this.BASEURL}/dac-docs/update/${id}`, dacUpdateDto);
  }

  getMonnaies(): Observable<IResponse<Currency>> {
    return this.httpClient.get<IResponse<Currency>>(`${this.BASEURL_PARAMS}/monnaie`);
  }

  getDacValidationData(id: number, version?: number): Observable<DacDocsFormResponse[]> {
    return this.httpClient.get<DacDocsFormResponse[]>(`${this.BASEURL}/dac-docs/${id}/${version}`);
  }

  uploadDacStaticFile(formData: FormData): Observable<any> {
    return this.httpClient.post<any>(`${this.BASEURL}/dac-docs/upload-static`, formData);
  }

  getPpmsDac(params?: PpmQuery): Observable<IResponsePaginate<Ppm>> {
    return this.httpClient.get<IResponsePaginate<Ppm>>(`${this.BASEURL_PPM}/ppms/for-dac`, { params: params as HttpParams });
  }

  generateListPdf(params: Pick<DacListRequest, 'dacId' | 'dacVer'> | DacListRequest): Observable<Blob> {
    return this.httpClient.post(`${this.BASEURL}/dac/generate-list-pdf`, params, {
      responseType: 'blob',
    });
  }

  generateSpecificDocx(previewDocDac: PreviewDocDac): Observable<Blob> {
    return this.httpClient.post(
      `${this.BASEURL}/dac/generate-specific-docx`,
      { ...previewDocDac, generateDocx: true }, //TODO
      {
        responseType: 'blob',
      }
    );
  }

  previewSpecificPdf(previewDocDac: PreviewDocDac): Observable<PreviewDocDacResponse> {
    return this.httpClient.post<PreviewDocDacResponse>(`${this.BASEURL}/dac/previsualiser-specific-pdf`, previewDocDac);
  }

  previewListPdf(params: Pick<DacListRequest, 'dacId' | 'dacVer'> | DacListRequest): Observable<PreviewDocDacResponse> {
    return this.httpClient.post<PreviewDocDacResponse>(`${this.BASEURL}/dac/previsualiser-list-pdf`, params);
  }

  generateSpecificPdf(previewDocDac: PreviewDocDac): Observable<Blob> {
    return this.httpClient.post(`${this.BASEURL}/dac/generate-specific-pdf`, previewDocDac, {
      responseType: 'blob',
    });
  }

  generateAaoPdf(previewAaoDynaDoc: PreviewAaoDynaDoc): Observable<Blob> {
    return this.httpClient.post(`${this.BASEURL}/dac/generate-aao-pdf`, previewAaoDynaDoc, {
      responseType: 'blob',
    });
  }

  generateDacPdf(dacId: number, type: string): Observable<Blob> {
    return this.httpClient.get(`${this.BASEURL}/dac/get-dac-pdf/${dacId}/${type}`, {
      responseType: 'blob',
    });
  }

  startWorkflow(id: number): Observable<IResponse<DacResponse>> {
    return this.httpClient.post<IResponse<DacResponse>>(`${this.BASEURL}/flowable/start/${id}`, {});
  }

  getDacDetails(id: number): Observable<DacDetails> {
    return this.httpClient.get<DacDetails>(`${this.BASEURL}/flowable/dac/${id}/with-task`);
  }

  uploadDacModel(taskId: string, dacFormData: FormData): Observable<IResponse<DacDetails>> {
    return this.httpClient.post<IResponse<DacDetails>>(`${this.BASEURL}/flowable/tasks/${taskId}/validate_with_model_dac`, dacFormData);
  }

  // crud dac doc submission

  getAllDacDocSubmissions(dacId: number, params: BaseQueryParams): Observable<IResponsePaginate<DacDocSubmission>> {
    return this.httpClient.get<IResponsePaginate<DacDocSubmission>>(`${this.BASEURL_PARAMS}/dac-doc-soumission/dac/${dacId}`, {
      params: params as HttpParams,
    });
  }

  createDacDocSubmission(docSubmission: DacDocSubmissionDto): Observable<WithMetadata<DacDocSubmission>> {
    return this.httpClient.post<WithMetadata<DacDocSubmission>>(`${this.BASEURL_PARAMS}/dac-doc-soumission`, docSubmission);
  }

  updateDacDocSubmission(id: number, dacId: number, date: Date): Observable<DocSubmission> {
    return this.httpClient.put<DocSubmission>(`${this.BASEURL_PARAMS}/dac-doc-soumission/dac/${dacId}/doc-soumission/${id}`, date);
  }

  getDocSubmissionNotInDacByType(dacId: number, docTypeId: number): Observable<WithMetadata<DocSubmission>[]> {
    return this.httpClient.get<WithMetadata<DocSubmission>[]>(
      `${this.BASEURL_PARAMS}/dac-doc-soumission/not-in-dac/${dacId}?typeDossierId=${docTypeId}`
    );
  }

  getDocSubmissionInDacByType(dacId: number, docTypeId: number): Observable<WithMetadata<DocSubmission>[]> {
    return this.httpClient.get<WithMetadata<DocSubmission>[]>(`${this.BASEURL_PARAMS}/dac-doc-soumission/in-dac/${dacId}?typeDossierId=${docTypeId}`);
  }

  deleteDacDocSubmission(id: number, dacId: number): Observable<DacDocSubmission> {
    return this.httpClient.delete<DacDocSubmission>(`${this.BASEURL_PARAMS}/dac-doc-soumission/dac/${dacId}/doc-soumission/${id}`);
  }

  //dynamic-form
  getAttachedDynamicForms(query: { dacId: number; dacVer: number }): Observable<IResponsePaginate<AttachedForm>> {
    return this.httpClient.post<IResponsePaginate<AttachedForm>>(`${this.BASEURL_PARAMS}/dac-dyna-form/attached`, query);
  }

  getAllDynamicFormFields(detachedFormDto: DetachedFormDto): Observable<WithMetadata<AttachedField>[]> {
    return this.httpClient.post<WithMetadata<AttachedField>[]>(`${this.BASEURL_PARAMS}/dac-dyna-form/fields`, detachedFormDto);
  }

  getUnattachedDynamicForms(query: { dacId: number; dacVer: number }, docTypeId?: number): Observable<WithMetadata<DynamicForm>[]> {
    return this.httpClient.post<WithMetadata<DynamicForm>[]>(`${this.BASEURL_PARAMS}/dac-dyna-form/unattached?typeDossierId=${docTypeId}`, query);
  }

  attachTransactionalForm(attachedFormDto: Omit<AttachedFormDto, 'dynaFormVer' | 'dynaFormFieldsId'>): Observable<WithMetadata<AttachedForm>> {
    return this.httpClient.post<WithMetadata<AttachedForm>>(`${this.BASEURL_PARAMS}/dac-dyna-form/attach`, attachedFormDto);
  }

  detachTransactionalForm(detachedFormDto: DetachedFormDto): Observable<WithMetadata<AttachedForm>> {
    return this.httpClient.delete<WithMetadata<AttachedForm>>(`${this.BASEURL_PARAMS}/dac-dyna-form/delete`, { body: detachedFormDto });
  }

  updateAttachedForm(attachedFormDto: AttachedFormDto): Observable<WithMetadata<AttachedForm>> {
    return this.httpClient.put<WithMetadata<AttachedForm>>(`${this.BASEURL_PARAMS}/dac-dyna-form/update-display`, attachedFormDto);
  }
  getAllPublishedDacsSoftForReportRequests(): Observable<DacResponseSoft> {
    return this.httpClient.get<DacResponseSoft>(`${this.BASEURL}/dac/by-current-user-ac-for-report`);
  }

  //dynamic-doc-copy
  getAllActiveLastCopies(query: { dacId: number; dacVer: number }): Observable<DynamicDocCopy[]> {
    return this.httpClient.get<DynamicDocCopy[]>(`${this.BASEURL}/dyna-docs-copy/active-last-version`, { params: query });
  }

  uploadModified(id: number, formData: FormData) {
    return this.httpClient.post(`${this.BASEURL}/dyna-docs-copy/upload-modified/${id}`, formData);
  }

  downloadModified(id: number): Observable<Blob> {
    return this.httpClient.get(`${this.BASEURL}/dyna-docs-copy/download-modified/${id}`, { responseType: 'blob' });
  }

  validateReporterReview(taskId: string, dacFormData: FormData): Observable<IResponse<void>> {
    return this.httpClient.post<IResponse<void>>(`${this.BASEURL}/flowable/tasks/${taskId}/validate_avis_note_rapporteur`, dacFormData);
  }

  // param-general-dac-values
  getParamGeneralDacValues(dacId: number, dacVer: number): Observable<ParamGeneralDacValue[]> {
    return this.httpClient.get<ParamGeneralDacValue[]>(`${this.BASEURL}/param-general-dac-values/${dacId}/${dacVer}`);
  }

  updateParamGeneralDacValuesBatch(dtos: ParamGeneralDacValueDto[]): Observable<ParamGeneralDacValueDto[]> {
    return this.httpClient.put<ParamGeneralDacValueDto[]>(`${this.BASEURL}/param-general-dac-values/batch`, dtos);
  }

  updateDacScenarioConfig(dacId: number, dacVer: number, dacUpdateConfigDto: DacUpdateConfigDto): Observable<DacResponse> {
    return this.httpClient.put<DacResponse>(`${this.BASEURL}/dac/${dacId}/${dacVer}/scenario`, dacUpdateConfigDto);
  }

  updateDacDates(
    dacId: number,
    dto: { dateDebutDepot: string; dateLimiteRecptionDemande: string; dateLimiteSoumission: string; dateOuverture: string }
  ): Observable<DacResponse> {
    return this.httpClient.put<DacResponse>(`${this.BASEURL}/dac/${dacId}/dates`, dto);
  }

  submitBiddersList(aoId: number, request: any): Observable<void> {
    return this.httpClient.post<void>(`${this.BASEURL}/soumission/appel-offre/${aoId}/bidders-list`, request);
  }

  getBiddersList(aoId: number): Observable<{ bidders: any[] }> {
    return this.httpClient.get<{ bidders: any[] }>(`${this.BASEURL}/soumission/appel-offre/${aoId}/bidders-list`);
  }
}
